import { supabase } from './supabase'
import { podePedirPermissao, suportaPush } from './instalacao'
import { mensagemDeErro } from './utilitarios'

/**
 * Assinatura de Web Push.
 *
 * Sem intermediário: o protocolo Web Push com chaves VAPID fala direto
 * com o serviço da Apple e do Google. Não há OneSignal nem Firebase no
 * caminho, e por isso o envio não custa nada em nenhuma escala.
 */

const CHAVE_VAPID_PUBLICA = import.meta.env.VITE_VAPID_PUBLIC_KEY

/** A chave VAPID vem em base64url; o PushManager quer bytes. */
function base64UrlParaBytes(base64Url: string) {
  const preenchimento = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + preenchimento).replace(/-/g, '+').replace(/_/g, '/')
  const bruto = atob(base64)
  return Uint8Array.from(bruto, (c) => c.charCodeAt(0))
}

function bytesParaBase64Url(buffer: ArrayBuffer | null) {
  if (!buffer) return ''
  const bytes = new Uint8Array(buffer)
  let binario = ''
  for (const byte of bytes) binario += String.fromCharCode(byte)
  return btoa(binario).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export type ResultadoAssinatura =
  | { estado: 'ok' }
  | { estado: 'sem_suporte' }
  | { estado: 'precisa_instalar' }
  | { estado: 'negada' }
  | { estado: 'erro'; mensagem: string }

/**
 * Pede permissão e registra a assinatura no banco.
 *
 * Precisa ser chamada a partir de um gesto da usuária — o iOS recusa o
 * pedido de permissão fora de um handler de toque.
 */
export async function ativarNotificacoes(): Promise<ResultadoAssinatura> {
  if (!suportaPush()) return { estado: 'sem_suporte' }
  if (!podePedirPermissao()) return { estado: 'precisa_instalar' }
  if (!CHAVE_VAPID_PUBLICA) {
    return { estado: 'erro', mensagem: 'VITE_VAPID_PUBLIC_KEY não configurada.' }
  }

  try {
    const permissao = await Notification.requestPermission()
    if (permissao !== 'granted') return { estado: 'negada' }

    await registrarAssinatura()
    return { estado: 'ok' }
  } catch (erro) {
    return { estado: 'erro', mensagem: mensagemDeErro(erro) }
  }
}

/**
 * Cria (ou recupera) a assinatura e faz upsert no banco.
 *
 * Chamada também a cada abertura do app: o iOS invalida assinaturas em
 * silêncio, sem notificar o servidor, e sem essa reinscrição o push
 * simplesmente para de chegar sem que ninguém perceba.
 */
export async function registrarAssinatura() {
  if (!suportaPush() || Notification.permission !== 'granted') return
  if (!CHAVE_VAPID_PUBLICA) return

  const registro = await navigator.serviceWorker.ready

  const assinatura =
    (await registro.pushManager.getSubscription()) ??
    (await registro.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlParaBytes(CHAVE_VAPID_PUBLICA),
    }))

  const { data: sessao } = await supabase.auth.getSession()
  const userId = sessao.session?.user.id
  if (!userId) return

  await supabase.from('push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: assinatura.endpoint,
      p256dh: bytesParaBase64Url(assinatura.getKey('p256dh')),
      auth: bytesParaBase64Url(assinatura.getKey('auth')),
      user_agent: navigator.userAgent,
      visto_em: new Date().toISOString(),
      invalidada_em: null,
    },
    { onConflict: 'endpoint' },
  )
}

/** Desliga as notificações neste dispositivo. */
export async function desativarNotificacoes() {
  const registro = await navigator.serviceWorker.ready
  const assinatura = await registro.pushManager.getSubscription()
  if (!assinatura) return

  await supabase.from('push_subscriptions').delete().eq('endpoint', assinatura.endpoint)
  await assinatura.unsubscribe()
}

/**
 * Confirma que uma notificação foi aberta.
 *
 * O service worker registra a exibição; aqui registramos o clique. Com
 * a turma pequena, esses dois carimbos são o único jeito de saber se o
 * push no iOS está mesmo entregando antes de escalar o programa.
 */
export async function confirmarCliqueNotificacao(idLog: string) {
  await supabase
    .from('notificacoes_log')
    .update({ clicado_em: new Date().toISOString() })
    .eq('id', idLog)
}
